import React, { useState, useEffect } from 'react';
import {
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
	CButton, CSpinner, CRow, CCol, CFormLabel, CBadge,
} from '@coreui/react';
import { useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { STATUS_COLORS, STATUS_LABELS, SENDER_TYPE_COLORS, SENDER_TYPE_LABELS, formatDateTime } from 'src/constants/supportLetters';
import { getCachedRequest, setCachedRequest } from 'src/utils/supportRequestCache';
import { MediaViewerButton } from 'src/components/myComponents/MediaViewerModal';
import SupportAttachmentImportNotice from './SupportAttachmentImportNotice';

/**
 * Модальне вікно швидкого перегляду запиту підтримки.
 * Підтримує кешування — при відкритті показує дані миттєво якщо вони вже в кеші.
 *
 * @param {boolean} open - чи відкрите вікно
 * @param {number|null} requestId - ID запиту для завантаження
 * @param {function} onClose - callback для закриття
 * @param {boolean} [readOnly] - якщо true, ховає кнопку «Відкрити повністю» (наприклад, при відкритті з каскадного вікна)
 */
const SupportLettersModal = ({ open, requestId, onClose, readOnly = false }) => {
	const navigate = useNavigate();
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open || !requestId) {
			setData(null);
			return;
		}

		// Показати з кешу якщо є — без спінера
		const cached = getCachedRequest(requestId);
		if (cached) {
			setData(cached);
			return;
		}

		// Завантажити і зберегти в кеш
		setLoading(true);
		AxiosInstance.get(`/support-requests/${requestId}`)
			.then(res => {
				setData(res.data);
				setCachedRequest(requestId, res.data);
			})
			.catch(() => setData(null))
			.finally(() => setLoading(false));
	}, [open, requestId]);

	const handleOpenFull = () => {
		onClose();
		navigate(`/support-letters/view/${requestId}`);
	};

	return (
		<CModal visible={open} onClose={onClose} size="lg">
			<CModalHeader>
				<CModalTitle>
					{data ? `Запит #${data.id} — ${data.subject || '(без теми)'}` : 'Запит підтримки'}
				</CModalTitle>
			</CModalHeader>
			<CModalBody>
				{loading && (
					<div className="d-flex justify-content-center py-4">
						<CSpinner color="secondary" />
					</div>
				)}
				{!loading && data && (
					<CRow className="g-3">
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Email</CFormLabel>
							<div>{data.user_email || '—'}</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Ім'я</CFormLabel>
							<div>{data.user_name || '—'}</div>
						</CCol>
						{data.user_phone && (
							<CCol md={6}>
								<CFormLabel className="fw-semibold">Телефон</CFormLabel>
								<div>{data.user_phone}</div>
							</CCol>
						)}
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Канал</CFormLabel>
							<div>{data.channel?.name || '—'}</div>
						</CCol>
						{data.call_center_id && (
							<CCol md={6}>
								<CFormLabel className="fw-semibold">ID в кол-центрі</CFormLabel>
								<div>{data.call_center_id}</div>
							</CCol>
						)}
						{data.account && (
							<CCol md={6}>
								<CFormLabel className="fw-semibold">Особовий рахунок</CFormLabel>
								<div>{data.account}</div>
							</CCol>
						)}
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Статус</CFormLabel>
							<div>
								<CBadge color={STATUS_COLORS[data.status] || 'secondary'}>
									{STATUS_LABELS[data.status] || data.status || '—'}
								</CBadge>
							</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Від</CFormLabel>
							<div>
								<CBadge color={SENDER_TYPE_COLORS[data.sender_type] || 'secondary'}>
									{SENDER_TYPE_LABELS[data.sender_type] || data.sender_type || '—'}
								</CBadge>
							</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Дата отримання</CFormLabel>
							<div>{formatDateTime(data.sent_at || data.created_at)}</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Дата внесення</CFormLabel>
							<div>{formatDateTime(data.created_at)}</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Останнє оновлення</CFormLabel>
							<div>{formatDateTime(data.updated_at)}</div>
						</CCol>
						<CCol xs={6}>
							<CFormLabel className="fw-semibold">Команди відповідальних</CFormLabel>
							<div>
								{(data.responders || []).length === 0
									? <span className="text-muted">—</span>
									: (data.responders || []).map(r => (
										<CBadge key={r.team_id} color="info" className="me-2">
											{r.team_name} ({r.team_code})
										</CBadge>
									))
								}
							</div>
						</CCol>
						<CCol xs={6}>
							<CFormLabel className="fw-semibold">Категорії</CFormLabel>
							<div>
								{(data.categories || []).length === 0
									? <span className="text-muted">—</span>
									: (data.categories || []).map(c => (
										<CBadge key={c.id} color="secondary" className="me-2">{c.name}</CBadge>
									))
								}
							</div>
						</CCol>
						<CCol md={12}>
							<CFormLabel className="fw-semibold">Тема</CFormLabel>
							<div>{data.subject || '—'}</div>
						</CCol>
						{data.message && (
							<CCol xs={12}>
								<CFormLabel className="fw-semibold">Повідомлення</CFormLabel>
								<div
									className="form-control"
									style={{ background: '#f8f9fa', minHeight: '80px', whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}
								>
									{data.message}
								</div>
							</CCol>
						)}
						{((data.attachments || []).length > 0 || (data.attachment_imports || []).length > 0) && (
							<CCol xs={12}>
								<CFormLabel className="fw-semibold">Вкладення</CFormLabel>
								<SupportAttachmentImportNotice
									imports={data.attachment_imports}
									requestId={data.id}
								/>
								{(data.attachments || []).length > 0 && (
									<div className="d-flex align-items-center gap-2">
										<MediaViewerButton
											attachments={data.attachments}
											className="media-viewer-button--bordered"
											title="Переглянути вкладення листа"
										/>
										<span className="text-muted small">Додано вкладень: {data.attachments.length}</span>
									</div>
								)}
							</CCol>
						)}
					</CRow>
				)}
				{!loading && !data && requestId && (
					<p className="text-danger">Не вдалося завантажити дані запиту.</p>
				)}
			</CModalBody>
			<CModalFooter>
				{!readOnly && (
					<CButton color="primary" onClick={handleOpenFull} title="Відкрити повну сторінку запиту">
						Відкрити повністю →
					</CButton>
				)}
				<CButton color="secondary" variant="outline" onClick={onClose} title="Закрити вікно швидкого перегляду">
					Закрити
				</CButton>
			</CModalFooter>
		</CModal>
	);
};

export default SupportLettersModal;
